import { useState } from "react";
import { Link } from "react-router-dom";

export default function SiteFooter() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const groups = [
    {
      title: "Shop",
      items: [
        { label: "Outerwear", to: "/shop/outerwear" },
        { label: "Footwear", to: "/shop/footwear" },
        { label: "Packs", to: "/shop/packs" },
        { label: "Tops", to: "/shop/tops" },
        { label: "Accessories", to: "/shop/accessories" },
      ],
    },
    {
      title: "Explore",
      items: [
        { label: "Our Story", to: "/our-story" },
        { label: "Journal", to: "/journal" },
        { label: "Technology", to: "/technology" },
        { label: "Sustainability", to: "/our-story" },
      ],
    },
    {
      title: "Support",
      items: [
        { label: "Contact", to: "/contact" },
        { label: "Shipping", to: "/contact" },
        { label: "Returns", to: "/contact" },
        { label: "Size Guide", to: "/contact" },
        { label: "Warranty", to: "/contact" },
      ],
    },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");
    const form = e.currentTarget;
    const fd = new FormData(form);
    if ((fd.get("website_alt") as string | null)?.trim()) {
      setStatus("success");
      setEmail("");
      return;
    }
    fd.delete("website_alt");
    setStatus("loading");
    try {
      const res = await fetch("https://readdy.ai/api/form/danntdlvmcl80jn29acg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      const msg = parsed?.meta?.message || parsed?.meta?.detail || text;
      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        setEmail("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(msg || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setFormError("Network error. Please try again.");
    }
  };

  return (
    <footer className="bg-footer pt-11 md:pt-16 pb-8">
      <div className="w-full px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pb-10 border-b border-white/10">
          <div className="lg:col-span-5">
            <p className="text-[11px] tracking-[0.24em] uppercase text-foreground-200 mb-4">Field Notes</p>
            <h3 className="text-[20px] md:text-[26px] leading-[1.15] tracking-[-0.01em] text-foreground-50 font-light max-w-md">
              New releases and quiet drops — no noise, once a month.
            </h3>
          </div>
          <div className="lg:col-span-7 flex items-end">
            <form onSubmit={handleSubmit} data-readdy-form id="newsletter-form-footer" className="w-full max-w-xl ml-auto">
              <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />
              <div className="flex items-center border-b border-white/25 focus-within:border-white/60 transition">
                <input
                  type="email"
                  name="email"
                  required
                  maxLength={200}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 bg-transparent py-4 text-[14px] md:text-[15px] text-foreground-50 placeholder:text-foreground-400 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="ml-4 flex items-center gap-2 text-[13px] tracking-[0.15em] uppercase text-foreground-50 hover:text-primary-400 transition cursor-pointer whitespace-nowrap disabled:opacity-60 py-4"
                >
                  {status === "loading" ? "Sending…" : "Subscribe"}
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
              <div className="min-h-[20px] mt-3 text-xs">
                {status === "success" && <span className="text-foreground-300">Thanks — you're on the list.</span>}
                {status === "error" && <span className="text-primary-400">{formError}</span>}
              </div>
            </form>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-12 gap-x-8 gap-y-9 py-9 md:py-12">
          <div className="col-span-2 md:col-span-4">
            <span className="text-[18px] md:text-[15px] font-medium tracking-[0.22em] text-foreground-50 block mb-4 md:mb-5 whitespace-nowrap">NORTHVALE</span>
            <p className="text-[13px] md:text-[12px] text-foreground-300 leading-[1.5] max-w-xs mb-5">
              Outdoor essentials for hikers, weekend campers and the long trail ahead. Designed in the alpine, tested on the ground.
            </p>
          </div>

          {groups.map((g) => (
            <div key={g.title} className="md:col-span-2">
              <h4 className="text-[11px] tracking-[0.20em] uppercase text-foreground-50 mb-5 md:mb-5">{g.title}</h4>
              <ul className="space-y-4 md:space-y-3">
                {g.items.map((it) => (
                  <li key={it.label}>
                    <Link to={it.to} className="text-[14px] md:text-[12px] leading-[1.35] text-foreground-300 hover:text-foreground-50 transition cursor-pointer whitespace-nowrap">
                      {it.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="md:col-span-2">
            <h4 className="text-[11px] tracking-[0.20em] uppercase text-foreground-50 mb-5 md:mb-5">Social</h4>
            <ul className="space-y-4 md:space-y-3">
              {[
                { label: "Instagram", icon: "instagram-line" },
                { label: "YouTube", icon: "youtube-line" },
                { label: "Spotify", icon: "spotify-line" },
              ].map((s) => (
                <li key={s.label}>
                  <a href="#" className="text-[14px] md:text-[12px] leading-[1.35] text-foreground-300 hover:text-foreground-50 transition cursor-pointer inline-flex items-center gap-2 whitespace-nowrap">
                    <i className={`ri-${s.icon}`}></i> {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-5 sm:gap-4 pt-7 md:pt-6 border-t border-white/10 text-[13px] md:text-[11px] text-foreground-300">
          <p className="whitespace-nowrap">© 2026 Northvale Outdoor Co.</p>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-foreground-50 cursor-pointer whitespace-nowrap">Privacy</a>
            <a href="#" className="hover:text-foreground-50 cursor-pointer whitespace-nowrap">Terms</a>
            <a href="#" className="hover:text-foreground-50 cursor-pointer whitespace-nowrap">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
}